import html2canvas, { type Options } from "html2canvas";
import { crossOriginBustedSrc } from "./crossOriginCacheBust.mjs";

// A cross-origin image already in the HTTP cache from a plain (no-CORS) load can be replayed
// for the crossOrigin="anonymous" request too, so the CORS load fails even against a bucket
// that now sends the headers. Vary the URL — in the clone only, so the live page is untouched.
const bustCrossOriginImages = (doc: Document): void => {
  doc.querySelectorAll("img").forEach((img) => {
    const busted = crossOriginBustedSrc(img.getAttribute("src"), window.location.origin);
    if (busted) img.setAttribute("src", busted);
  });
};

const withCors = (options: Partial<Options>, useCORS: boolean): Partial<Options> => ({
  ...options,
  useCORS,
  onclone: (doc: Document, el: HTMLElement) => {
    options.onclone?.(doc, el);
    bustCrossOriginImages(doc);
  },
});

/**
 * html2canvas wrapper that actually renders cross-origin images (team/company logos, remote
 * artwork) instead of silently dropping them.
 *
 * Without `useCORS` html2canvas refuses to draw any cross-origin image and leaves a blank gap —
 * no error, no console warning, which is why blank logos in exported PDFs went unnoticed. With
 * `useCORS` it re-fetches them as crossOrigin="anonymous", but against a bucket that serves no
 * CORS headers that fetch rejects the *entire* render, which is worse than one missing logo.
 * So: try with CORS, and on failure retry without. Every html2canvas call should go through
 * this rather than calling the library directly.
 */
export async function captureElement(
  el: HTMLElement,
  options: Partial<Options> = {},
): Promise<HTMLCanvasElement> {
  try {
    return await html2canvas(el, withCors(options, true));
  } catch {
    return await html2canvas(el, withCors(options, false));
  }
}
